// Palworld OFFICIAL CARD GAME deck construction guide pack.
//
// Deliberately distinct from the existing packs so nothing duplicates:
//   - guides-tcg-longtail "palworld-tcg-starter-deck-list" = a buy-and-play list
//   - guides-tcg-strategy "best-deck" article = per-Pal meta picks
//   - guides-more "palworld-trial-deck" = TD01/TD02 product contents
// This pack covers construction *rules and ratios*: legal deck size, colour
// commitment, the cost curve, tuning between events, and a build FAQ.
//
// Ships EXACTLY 5 AEO/SEO-optimized articles with direct-answer intros and a
// rotating pool of official external links per article.
import type { Guide, ExternalLink } from "./guides-data";

const L = {
  rules: {
    label: "Palworld OFFICIAL CARD GAME — rules",
    url: "https://en.palworld-official-cardgame.com/rule",
    note: "Official deck construction limits, turn structure and timing rules.",
  },
  cardlist: {
    label: "Palworld OFFICIAL CARD GAME — card list",
    url: "https://en.palworld-official-cardgame.com/cardlist",
    note: "Official English card list with printed card numbers and rarities.",
  },
  news: {
    label: "Palworld OFFICIAL CARD GAME — news",
    url: "https://en.palworld-official-cardgame.com/news",
    note: "Product releases, errata and organized-play announcements.",
  },
} as const;

const pool = (): ExternalLink[] => [L.rules, L.cardlist, L.news];

export const TCG_DECKBUILDING_GUIDES: Guide[] = [
  {
    slug: "palworld-tcg-deck-building",
    title: "Palworld TCG Deck Building Guide & FAQ",
    desc: "How to build a legal, consistent Palworld OFFICIAL CARD GAME deck: deck size and copy limits, colour commitment, the cost curve, Pal-to-event ratios, Soul cards and how to tune between events.",
    tag: "TCG Guide",
    updated: "2026-11-16",
    keywords:
      "palworld tcg deck building, palworld deck building guide, how many cards in a palworld deck, palworld tcg deck size, palworld deck ratios, palworld tcg cost curve, palworld soul cards deck, palworld tcg mulligan, palworld deck list rules, palworld tcg two colour deck",
    articles: [
      {
        title: "How many cards are in a Palworld TCG deck?",
        slug: "deck-size-and-copy-limits",
        intro:
          "A tournament-legal Palworld OFFICIAL CARD GAME deck is a fixed 50-card main deck plus its Soul deck, and you may include at most 4 copies of any one card name. Parallel printings such as SR, SP, OSR, SSP and TSP share the card name of the base print, so an EBP01-001 and an EBP01-001OSR still count toward the same 4-copy limit.",
        sections: [
          {
            heading: "Main deck",
            text: "Build exactly the required main-deck count — no more, no less. Running over is the single most common deck-check failure at organized play, so count your list twice before you register it in the deck log.",
          },
          {
            heading: "The 4-copy rule counts names, not codes",
            text: "Copy limits apply per card name. Mixing a base EBP01-011 with its parallel printings does not let you exceed four; it only changes how the deck looks. Buy parallels for display value, not for extra copies.",
          },
          {
            heading: "Soul deck",
            text: "Soul cards live in their own zone and are registered separately from the main deck. Check the current official rules document for the exact Soul count before an event, because that number is set by rules updates rather than by the set you are playing.",
          },
          {
            heading: "Language and printing legality",
            text: "Cards must be identifiable and, at English-language events, in a printing the judge can read. Japanese prints of the same card are usually allowed with an official translation on hand — confirm with the event organizer first.",
          },
          {
            heading: "Sleeves and condition",
            text: "All cards need identical opaque-backed sleeves. A scratched or bent card that is identifiable from the back is a marked-card problem, so swap heavily played copies before you register.",
          },
        ],
        externalLinks: pool(),
      },
      {
        title: "One colour or two? Choosing your Palworld deck colours",
        slug: "colour-commitment",
        intro:
          "Single-colour Palworld decks are the most consistent because every card is always castable, while two-colour decks buy stronger top-end Pals and better answers at the cost of some dead early draws. For a first competitive build, commit roughly 70% of the deck to a primary colour and keep the secondary colour to cheap, high-impact cards only.",
        sections: [
          {
            heading: "Why mono is the default",
            text: "A mono-colour list never strands a card in hand. If you are learning the game, or building for a Trial-Deck-level event, mono is the fastest route to a deck that does what it says every game.",
          },
          {
            heading: "When a second colour pays for itself",
            text: "Add a second colour when it gives you something the first colour genuinely lacks — removal, a mount-style attacker, or a cheap defensive body — not simply because a card is rare or expensive.",
          },
          {
            heading: "Keep the splash cheap",
            text: "Splashed cards should sit at the low end of the cost curve so they are still playable on the turn they arrive. Expensive off-colour top-end is the main cause of unwinnable hands.",
          },
          {
            heading: "Elemental coverage",
            text: "Use the Pal Stars elemental chart at /elements to check that your colour pair is not blind to a whole element, then confirm each matchup with the damage calculator at /damage before you lock a list.",
          },
          {
            heading: "Test three colours only with a payoff",
            text: "Three colours is a trap unless a card explicitly rewards it. Every extra colour multiplies the number of hands where your best card is uncastable.",
          },
        ],
        externalLinks: pool(),
      },
      {
        title: "The Palworld TCG cost curve and Pal-to-event ratio",
        slug: "cost-curve-and-ratios",
        intro:
          "A reliable Palworld deck is roughly 55-60% Pals, 25-30% event cards and the rest support, with the Pal count weighted toward the cheap end: most lists want the majority of Pals at low cost, a mid-band of value plays, and only a handful of expensive finishers.",
        sections: [
          {
            heading: "Weight the curve low",
            text: "Cheap Pals let you contest the board on the earliest turns. Decks that skew expensive lose before their good cards matter, no matter how strong the top end reads.",
          },
          {
            heading: "Cap the finishers",
            text: "Keep genuinely expensive cards to a small handful. Two or three copies of a single big threat is usually enough, because drawing the second one early is a wasted card.",
          },
          {
            heading: "Event card density",
            text: "Event cards are your interaction. Too few and you cannot answer a developed board; too many and you have nothing of your own to develop. Around a quarter of the deck is the practical sweet spot.",
          },
          {
            heading: "Count your outs, not your favourites",
            text: "For every problem card in the format, count how many cards in your 50 actually answer it. If the number is under four, expect to lose that game roughly as often as you draw badly.",
          },
          {
            heading: "Check the list against real card data",
            text: "Cost, power and strike numbers for every printed card are listed at /cards under their exact printed codes, and the deck builder at /deck-builder totals your curve for you as you add cards.",
          },
        ],
        externalLinks: pool(),
      },
      {
        title: "Tuning a Palworld deck between events",
        slug: "tuning-and-sideboarding",
        intro:
          "Tune a Palworld deck in small increments: change no more than two or three cards after an event, and only after you can name the games those cards lost you. Track your opening hands and your mulligans, because most losses that feel like bad luck are a curve or ratio problem you can fix on paper.",
        sections: [
          {
            heading: "Keep a loss log",
            text: "After each match, write one line: what beat you and which card you wished you had drawn. Three lines saying the same thing is a real signal; a single blowout is not.",
          },
          {
            heading: "Change two cards, not ten",
            text: "Large rewrites destroy the information from your last event. Small, deliberate swaps let you attribute an improvement to the change you actually made.",
          },
          {
            heading: "Respect the mulligan",
            text: "Judge a card by whether you are happy to see it in your opening hand. Cards you routinely want to shuffle away are the first cut, regardless of rarity or price.",
          },
          {
            heading: "Practise before you buy",
            text: "Test list changes for free against the bot or a friend in the Pal Stars simulator at /tcg-simulator, then check what the upgrade costs on the price chart at /prices before you commit money.",
          },
          {
            heading: "Re-read the rules after every set",
            text: "Set releases and rules updates can change deck-construction limits and keyword timing. Confirm the current official rules document before each event rather than assuming last season's numbers.",
          },
        ],
        externalLinks: pool(),
      },
      {
        title: "Palworld TCG deck building FAQ",
        slug: "faq",
        intro:
          "The short answers: a legal deck is a fixed 50-card main deck plus a separate Soul deck, maximum 4 copies per card name, parallels share the base card's name, and there is no separate best-of-three sideboard in the Palworld OFFICIAL CARD GAME — you tune the registered list between events instead.",
        sections: [
          {
            heading: "Can I use parallel or promo cards in a tournament deck?",
            text: "Yes, provided they are official printings in legal condition and sleeved identically. Not-For-Sale promo printings such as the EPR and ESOUL cards are legal where the event's product legality allows them — confirm with the organizer.",
          },
          {
            heading: "Do I need a Soul deck to play?",
            text: "Casual games can be played without one, but organized play expects the Soul deck to be registered with the main deck. Build it deliberately: it is part of your plan, not an afterthought.",
          },
          {
            heading: "Is a Trial Deck good enough to start?",
            text: "Yes. TD01 and TD02 are legal, pre-tuned lists designed as an on-ramp; see the Trial Deck guide for their contents and the cheapest upgrades that actually matter.",
          },
          {
            heading: "How much does a competitive Palworld deck cost?",
            text: "Most of a list is commons and uncommons; the cost sits in a few chase cards. Check the current market and sold prices at /prices, and use the pack calculator at /pack-calculator to compare buying singles against opening sealed product.",
          },
          {
            heading: "Can I save and share a deck list?",
            text: "Yes — build it at /deck-builder, save it to your Pal Stars deck vault, and test it in the simulator at /tcg-simulator against the bot or an online opponent with a match code.",
          },
        ],
        externalLinks: pool(),
      },
    ],
  },
];
